/**
 * @file    sorting
 * @scope   SHARED
 * @summary Client-side sort functions for react-data-table (Strategy: hierarchy vs flat) and
 *          the legacy sorting-type resolution.
 * @consumes helpers/sorting
 * @produces resolveLegacySortingType, sortValues, createSortFunction
 * @sideEffects Sorts the given rows array in place (react-data-table contract).
 */
import { MutableRefObject } from 'react';
import { extractValue } from 'helpers/sorting';
import { TableType } from 'models/WidgetModel';

/** TODO: standardize payload key casing once the CX front end is fully deployed. */
export const resolveLegacySortingType = (payload: any) => {
  const isEnterprise = payload?.isEnterprise || payload?.IsEnterprise;
  const type = isEnterprise ? TableType.ENTERPRISE : TableType.NORMAL;

  let sortingType: string = TableType.NORMAL;
  if (payload?.isSortable || payload?.IsSortable) {
    sortingType = isEnterprise ? TableType.ENTERPRISE : TableType.NORMAL;
  } else if (payload?.QueriesMetadata?.length > 0) {
    sortingType = TableType.NONE;
  }
  return { type, sortingType };
};

/** Compares two values: numbers first, then strings (natural order), then the rest. */
export const sortValues = (aValue: any, bValue: any, direction: string) => {
  const desc = direction === 'desc';
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return desc ? bValue - aValue : aValue - bValue;
  }
  if (typeof aValue === 'number') return desc ? -1 : 1;
  if (typeof bValue === 'number') return desc ? 1 : -1;
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    const opts = { numeric: true, sensitivity: 'base' as const };
    return desc
      ? bValue.localeCompare(aValue, undefined, opts)
      : aValue.localeCompare(bValue, undefined, opts);
  }
  if (typeof aValue === 'string') return desc ? -1 : 1;
  if (typeof bValue === 'string') return desc ? 1 : -1;
  return 0;
};

type Comparator = (a: any, b: any, field: string, direction: string) => number;

const flatComparator: Comparator = (a, b, field, direction) =>
  sortValues(extractValue(a[field]), extractValue(b[field]), direction);

/** ENTERPRISE-ONLY: L1 rows (workspaces) stay put; L2 rows sort inside their workspace. */
const hierarchyComparator: Comparator = (a, b, field, direction) => {
  if (a.rowType == 'L1' || b.rowType == 'L1') return 0;
  if (a.workspaceId != b.workspaceId) return 0;
  const aValue =
    a[field]?.value !== undefined ? extractValue(a[field].value) : extractValue(a[field]);
  const bValue =
    b[field]?.value !== undefined ? extractValue(b[field].value) : extractValue(b[field]);
  return sortValues(aValue, bValue, direction);
};

/** Builds the react-data-table sortFunction keeping the totals row pinned on top. */
export const createSortFunction =
  (sortingType: string | undefined, showTotals: boolean, totalsFirstRow: MutableRefObject<any>) =>
  (rows: any[], selector: (arg0: any) => any, direction: string) => {
    const comparator = sortingType === TableType.ENTERPRISE ? hierarchyComparator : flatComparator;
    const rowCollection = showTotals ? rows.slice(1) : rows;
    const { field } = selector([rowCollection]);

    rowCollection.sort((a, b) => comparator(a, b, field, direction));

    if (showTotals && totalsFirstRow.current != null) {
      rowCollection.unshift(totalsFirstRow.current);
    }
    return rowCollection;
  };
